// src/pages/StudentProgressCheckup.jsx
// Student Self Progress Checkup
// Fields per entry: Category | Topic Name | Logged (HH:MM) | Target (HH:MM) | Notes
// Shows: category progress bars vs admin target, all today's entries, history

import { useState, useEffect, useRef } from "react";
import {
  saveProgressEntry, subscribeProgressLogs,
  subscribeProgressStats, getStudentTargets,
  deleteProgressEntry,
  subscribeCategories, subscribeTopics
} from "../firebase/database";
import { minutesToHHMM, HHMMToMinutes, todayStr } from "../utils/helpers";
import { theme } from "../utils/theme";
import TimelineView from "../components/TimelineView";
import DietLog from "../components/DietLog";
import { getStudent } from "../firebase/database";

const C = theme.dark;

// Smart HH:MM input — accepts "2", "2:3", "2:30", "230", "0900" → normalises on blur
function TimeInput({ value, onChange, placeholder = "00:00" }) {
  const [raw, setRaw] = useState(value || "");

  useEffect(() => { setRaw(value || ""); }, [value]);

  const normalise = (str) => {
    const digits = str.replace(/\D/g, "");
    if (!digits) return "";
    let h, m;
    if (str.includes(":") && str.length > 2) {
      const parts = str.split(":");
      h = parseInt(parts[0]) || 0;
      m = Math.min(59, parseInt(parts[1]) || 0);
    } else if (digits.length <= 2) {
      h = parseInt(digits); m = 0;
    } else if (digits.length === 3) {
      h = parseInt(digits.slice(0, 1));
      m = Math.min(59, parseInt(digits.slice(1)));
    } else {
      // For 4 or more digits, e.g., "0900" or "1230"
      h = parseInt(digits.slice(0, digits.length - 2));
      m = Math.min(59, parseInt(digits.slice(digits.length - 2)));
    }
    // Final boundary check for hours
    h = Math.min(23, h);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const handleBlur = () => {
    const norm = normalise(raw);
    setRaw(norm);
    onChange(norm);
  };

  return (
    <input
      value={raw}
      onChange={e => setRaw(e.target.value)}
      onBlur={handleBlur}
      inputMode="numeric"
      placeholder={placeholder}
      maxLength={5}
      style={{
        width: "100%", background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "11px 14px", color: C.text, fontSize: 16,
        outline: "none", fontFamily: "'DM Mono', monospace", letterSpacing: 3,
        textAlign: "center",
      }}
    />
  );
}

function Bar({ logged, target, color, height = 7 }) {
  const pct = target > 0 ? Math.min(100, Math.round(logged / target * 100)) : 0;
  return (
    <div style={{ height, borderRadius: height / 2, background: C.dim, overflow: "hidden" }}>
      <div style={{ width: pct + "%", height: "100%", background: color, borderRadius: height / 2, transition: "width .5s ease" }} />
    </div>
  );
}

function PctBadge({ pct, color }) {
  const bg = pct >= 100 ? C.greenDim : pct >= 75 ? color + "22" : pct >= 50 ? "#2E1F08" : "#2D1212";
  const fg = pct >= 100 ? C.green   : pct >= 75 ? color        : pct >= 50 ? C.amber   : C.red;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: bg, color: fg }}>
      {pct}%
    </span>
  );
}

export default function StudentProgressCheckup({ studentId, studentName, onDone, date: initialDate }) {
  const [categories, setCategories] = useState([]);
  const [allTopics, setAllTopics]   = useState([]);
  const [logs, setLogs]       = useState([]);
  const [stats, setStats]     = useState({});
  const [targets, setTargets] = useState({});
  const [saving, setSaving]   = useState(false);
  const [flash, setFlash]     = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("progress"); // "progress" | "diet"
  const [profile, setProfile] = useState(null);

  const [form, setForm] = useState({ category: "", topicName: "", startTime: "", endTime: "", notes: "" });
  const formRef = useRef(null);

  const [activeDate, setActiveDate] = useState(initialDate || todayStr());

  useEffect(() => {
    if (!studentId) return;
    getStudent(studentId).then(setProfile);
    const u1 = subscribeProgressLogs(studentId, setLogs);
    const u2 = subscribeProgressStats(studentId, setStats);
    const u3 = subscribeCategories(cats => {
        setCategories(cats);
        if (cats.length > 0) setForm(f => f.category ? f : { ...f, category: cats[0].id });
    });
    const u4 = subscribeTopics(setAllTopics);
    getStudentTargets(studentId).then(setTargets);
    return () => { u1(); u2(); u3(); u4(); };
  }, [studentId]);

  const todayLogs = logs.filter(l => l.date === activeDate);
  const pastLogs  = logs.filter(l => l.date !== activeDate);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Duration calculation
  const startMin = form.startTime ? HHMMToMinutes(form.startTime) : 0;
  const endMin   = form.endTime   ? HHMMToMinutes(form.endTime)   : 0;
  const duration = (endMin > startMin) ? (endMin - startMin) : 0;
  const durationHHMM = minutesToHHMM(duration);

  const canSave = form.category && form.topicName.trim() && duration > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);

    // If editing, preserve the original date of the entry
    const originalEntry = editingId ? logs.find(l => l.entryId === editingId) : null;
    const entryDate = originalEntry ? originalEntry.date : activeDate;

    // targetHHMM is now optional for the student (filled by admin)
    // We send loggedHHMM as the calculated duration
    await saveProgressEntry(studentId, { 
      ...form, 
      entryId: editingId, // If editingId is null, a new ID will be created
      loggedHHMM: durationHHMM, 
      targetHHMM: "00:00",
      date: entryDate
    });
    setForm({ ...form, topicName: "", startTime: "", endTime: "", notes: "" });
    setEditingId(null);
    setSaving(false);
    setFlash(true);
    setTimeout(() => setFlash(false), 2000);
  };

  const handleEdit = (entry) => {
    setEditingId(entry.entryId);
    setForm({
      category: entry.category,
      topicName: entry.topicName,
      startTime: entry.startTime,
      endTime: entry.endTime,
      notes: entry.notes || "",
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDelete = async (entry) => {
    if (!window.confirm("Are you sure you want to delete this log entry?")) return;
    setDeleting(entry.entryId);
    await deleteProgressEntry(studentId, entry.entryId, entry.category, entry.loggedMinutes, entry.date);
    setDeleting(null);
  };

  const activeCat = categories.find(c => c.id === form.category) || { color: C.accent };
  const filteredTopics = allTopics.filter(t => t.categoryId === form.category);

  // Preview bar for current form values vs Category Admin Target
  const adminTargetMinutes = targets[form.category]?.targetMinutes || 0;
  const currentLoggedStats = stats[form.category]?.totalLoggedMinutes || 0;
  // If editing, we subtract the previous duration from the total before adding new one
  const previousLoggedMinutes = editingId ? (todayLogs.find(l => l.entryId === editingId)?.loggedMinutes || 0) : 0;
  const previewTotal = currentLoggedStats - previousLoggedMinutes + duration;
  const showPreview  = duration > 0 && adminTargetMinutes > 0;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.text, maxWidth: 700, margin: "0 auto", padding: "0 0 40px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes slideDown { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:none} }
        @keyframes flashGreen { 0%,100%{background:#141826} 50%{background:#0D2E1C} }
        .slide { animation: slideDown .25s ease forwards; }
        input::placeholder, textarea::placeholder { color: #3A4058; }
        input:focus, textarea:focus { border-color: #2A3A60 !important; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700 }}>Self Progress Checkup</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
             <div style={{ fontSize: 13, color: C.muted }}>{studentName}</div>
             <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.dim }} />
             <input 
               type="date" 
               value={activeDate} 
               onChange={e => setActiveDate(e.target.value)}
               style={{ 
                 background: "none", border: "none", color: C.accent, 
                 fontSize: 13, fontWeight: 700, outline: "none", cursor: "pointer",
                 fontFamily: "DM Sans"
               }} 
             />
          </div>
        </div>
        {onDone && (
          <button 
            onClick={onDone} 
            style={{ 
              fontSize: 14, fontWeight: 700, color: "#fff", 
              background: C.red, border: "none", borderRadius: 10, 
              padding: "10px 20px", cursor: "pointer", 
              fontFamily: "DM Sans", boxShadow: "0 4px 12px rgba(255, 92, 92, 0.3)" 
            }}
          >
            Finish & Logout ⏻
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button 
          onClick={() => setActiveTab("progress")}
          style={{ 
            flex: 1, padding: "12px", borderRadius: 10, border: "none",
            background: activeTab === "progress" ? C.accent : C.dim,
            color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer"
          }}
        >
          Daily Work/Study Log
        </button>
        <button 
          onClick={() => setActiveTab("diet")}
          style={{ 
            flex: 1, padding: "12px", borderRadius: 10, border: "none",
            background: activeTab === "diet" ? C.green : C.dim,
            color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer"
          }}
        >
          Diet & Nutrition Log
        </button>
      </div>

      {activeTab === "diet" ? (
        <DietLog 
          studentId={studentId} 
          studentName={studentName} 
          dailyKcalTarget={profile?.dailyKcalTarget}
          date={activeDate}
        />
      ) : (
        <>
          {/* ── Timeline ── */}
          <TimelineView logs={todayLogs} categories={categories} date={activeDate} />

          {/* ── Entry form ── */}
          <div 
            ref={formRef}
            style={{
              background: flash ? "#0D2E1C" : C.card,
              border: `1px solid ${flash ? C.green + "44" : (editingId ? C.accent + "66" : C.border)}`,
              borderRadius: 13, padding: 22, marginBottom: 20,
              transition: "background .4s, border-color .4s",
              boxShadow: editingId ? `0 0 20px ${C.accent}15` : "none"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {editingId ? "Edit Log Entry" : "Log New Entry"}
                {editingId && (
                  <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginLeft: 8 }}>
                    {logs.find(l => l.entryId === editingId)?.date !== activeDate && `(${logs.find(l => l.entryId === editingId)?.date})`}
                  </span>
                )}
              </div>
              {editingId && (
                <button 
                  onClick={() => { setEditingId(null); setForm({ ...form, topicName: "", startTime: "", endTime: "", notes: "" }); }}
                  style={{ fontSize: 12, color: C.muted, background: "none", border: "none", cursor: "pointer" }}
                >
                  Cancel Edit
                </button>
              )}
            </div>

            {/* Category pills */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 9, fontWeight: 500, letterSpacing: .5 }}>CATEGORY</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {categories.map(cat => {
                  const active = form.category === cat.id;
                  const color  = cat.color || C.accent;
                  return (
                    <button key={cat.id} onClick={() => set("category", cat.id)} style={{
                      fontSize: 13, fontWeight: active ? 600 : 400,
                      padding: "7px 15px", borderRadius: 8, cursor: "pointer", fontFamily: "DM Sans",
                      border: `1px solid ${active ? color : C.border}`,
                      background: active ? color + "22" : "transparent",
                      color: active ? color : C.muted,
                      transition: "all .15s",
                    }}>
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Topic name */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontWeight: 500, letterSpacing: .5 }}>TARGET TOPIC NAME</div>
              <input
                list="topics-list"
                value={form.topicName}
                onChange={e => set("topicName", e.target.value)}
                placeholder="Select or type topic…"
                style={{
                  width: "100%", background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: "11px 14px", color: C.text, fontSize: 14,
                  outline: "none", fontFamily: "DM Sans",
                }}
              />
              <datalist id="topics-list">
                {filteredTopics.map(t => <option key={t.id} value={t.label} />)}
              </datalist>
            </div>

            {/* Time inputs */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: showPreview ? 12 : 16 }}>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontWeight: 500, letterSpacing: .5 }}>ACTIVITY START TIME</div>
                <TimeInput value={form.startTime} onChange={v => set("startTime", v)} placeholder="09:00" />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontWeight: 500, letterSpacing: .5 }}>ACTIVITY END TIME</div>
                <TimeInput value={form.endTime} onChange={v => set("endTime", v)} placeholder="10:30" />
              </div>
            </div>

            {/* Duration Display */}
            {duration > 0 && (
              <div style={{ marginBottom: 16, textAlign: "center", background: C.surface, padding: 10, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>CALCULATED DURATION</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.accent, fontFamily: "DM Mono" }}>{durationHHMM}</div>
              </div>
            )}

            {/* Live preview bar against Admin Category Target */}
            {showPreview && (
              <div className="slide" style={{ background: C.surface, borderRadius: 9, padding: "10px 14px", marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
                  <span style={{ color: C.muted }}>Overall {activeCat.label} progress {editingId ? "after edit" : "if saved"}</span>
                  <PctBadge pct={Math.min(100, Math.round(previewTotal / adminTargetMinutes * 100))} color={activeCat.color} />
                </div>
                <Bar logged={previewTotal} target={adminTargetMinutes} color={activeCat.color} height={8} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: C.muted }}>
                  <span>{minutesToHHMM(previewTotal)} logged total</span>
                  <span>of {minutesToHHMM(adminTargetMinutes)} admin target</span>
                </div>
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontWeight: 500, letterSpacing: .5 }}>NOTES (optional)</div>
              <textarea
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                rows={3}
                placeholder="What did you cover? Any doubts, key insights, or what to revise next…"
                style={{
                  width: "100%", background: C.surface, border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: "11px 14px", color: C.text, fontSize: 14,
                  outline: "none", fontFamily: "DM Sans", resize: "vertical", lineHeight: 1.6,
                }}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !canSave}
              style={{
                width: "100%", padding: "13px", borderRadius: 10, border: "none",
                fontSize: 15, fontWeight: 600, fontFamily: "DM Sans", cursor: canSave ? "pointer" : "not-allowed",
                background: flash ? C.green : canSave ? activeCat.color : C.dim,
                color: "#fff", transition: "background .3s",
              }}
            >
              {saving ? "Saving…" : flash ? "Entry saved ✓" : (editingId ? "Update Entry" : "Save Entry")}
            </button>
          </div>

          {/* ── Active Date entries ── */}
          {todayLogs.length > 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 13, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
                Logs for {activeDate === todayStr() ? "Today" : activeDate}
                <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginLeft: 8 }}>{todayLogs.length} logged</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {todayLogs.map(entry => {
                  const catObj = categories.find(c => c.id === entry.category);
                  const color = catObj?.color || C.accent;
                  const dim   = color + "1A";
                  const pct   = entry.targetMinutes > 0 ? Math.min(100, Math.round(entry.loggedMinutes / entry.targetMinutes * 100)) : null;
                  const catLabel = catObj?.label || entry.category;
                  const isEditing = editingId === entry.entryId;
                  
                  return (
                    <div key={entry.entryId} style={{ 
                      background: C.surface, 
                      borderRadius: 10, 
                      padding: "13px 15px", 
                      borderLeft: `3px solid ${color}`,
                      border: isEditing ? `1px solid ${color}66` : "none",
                      opacity: isEditing ? 0.7 : 1
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color, background: dim, padding: "2px 8px", borderRadius: 5 }}>{catLabel}</span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{entry.topicName}</span>
                          </div>
                          <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.muted, alignItems: "center", flexWrap: "wrap" }}>
                            <span>Time: <span style={{ color: C.text, fontFamily: "'DM Mono'" }}>{entry.startTime} - {entry.endTime}</span></span>
                            <span>Logged: <span style={{ color: C.text, fontFamily: "'DM Mono'", fontWeight: 500 }}>{minutesToHHMM(entry.loggedMinutes)}</span></span>
                            {entry.targetMinutes > 0 && (
                              <span>Target: <span style={{ color: C.text, fontFamily: "'DM Mono'" }}>{minutesToHHMM(entry.targetMinutes)}</span></span>
                            )}
                            {pct !== null && <PctBadge pct={pct} color={color} />}
                          </div>
                          {entry.targetMinutes > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <Bar logged={entry.loggedMinutes} target={entry.targetMinutes} color={color} height={5} />
                            </div>
                          )}
                          {entry.notes && (
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.6, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                              {entry.notes}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                          <button
                            onClick={() => handleEdit(entry)}
                            style={{ fontSize: 12, color: C.accent, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
                            title="Edit entry"
                          >
                            ✎
                          </button>
                          <button
                            onClick={() => handleDelete(entry)}
                            disabled={deleting === entry.entryId}
                            style={{ fontSize: 12, color: C.muted, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0, opacity: deleting === entry.entryId ? 0.4 : 1 }}
                            title="Delete entry"
                          >
                            {deleting === entry.entryId ? "…" : "✕"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Past entries toggle ── */}
          {pastLogs.length > 0 && (
            <div>
              <button
                onClick={() => setShowHistory(h => !h)}
                style={{ fontSize: 13, color: C.muted, background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontFamily: "DM Sans", width: "100%", marginBottom: showHistory ? 12 : 0 }}
              >
                {showHistory ? "Hide" : "Show"} past entries ({pastLogs.length})
              </button>
              {showHistory && (
                <div className="slide" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {pastLogs.map(entry => {
                    const catObj = categories.find(c => c.id === entry.category);
                    const color  = catObj?.color || C.accent;
                    const catLabel = catObj?.label || entry.category;
                    const pct      = entry.targetMinutes > 0 ? Math.min(100, Math.round(entry.loggedMinutes / entry.targetMinutes * 100)) : null;
                    const isEditing = editingId === entry.entryId;

                    return (
                      <div key={entry.entryId} style={{ 
                        background: C.card, 
                        borderRadius: 10, 
                        padding: "12px 15px", 
                        border: isEditing ? `1px solid ${color}66` : `1px solid ${C.border}`, 
                        borderLeft: `3px solid ${color}`,
                        opacity: isEditing ? 0.7 : 1
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "flex-start" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color }}>{catLabel}</span>
                            <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{entry.topicName}</span>
                          </div>
                          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono'" }}>{entry.date}</span>
                            <button
                              onClick={() => handleEdit(entry)}
                              style={{ fontSize: 12, color: C.accent, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
                              title="Edit entry"
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => handleDelete(entry)}
                              disabled={deleting === entry.entryId}
                              style={{ fontSize: 12, color: C.muted, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", opacity: deleting === entry.entryId ? 0.4 : 1 }}
                              title="Delete entry"
                            >
                              {deleting === entry.entryId ? "…" : "✕"}
                            </button>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 12, fontSize: 12, color: C.muted }}>
                          <span style={{ fontFamily: "'DM Mono'" }}>{entry.startTime}-{entry.endTime}</span>
                          <span>Logged: <span style={{ color: C.text, fontFamily: "'DM Mono'" }}>{minutesToHHMM(entry.loggedMinutes)}</span></span>
                          {entry.targetMinutes > 0 && <span>Target: <span style={{ color: C.text, fontFamily: "'DM Mono'" }}>{minutesToHHMM(entry.targetMinutes)}</span></span>}
                          {pct !== null && <PctBadge pct={pct} color={color} />}
                        </div>
                        {entry.notes && <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>{entry.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
